/**
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * progressStore.ts — 학습 진도 & 통계 전역 상태
 *
 * 【설계 원칙】 "어댑터 패턴" — 나중에 DB로 교체할 때
 *   storage.ts 의 save/load 함수만 API 호출로 바꾸면 됩니다.
 *
 * 【현재】 localStorage  →  【미래】 REST API / Supabase / Firebase
 *
 * 데이터 흐름:
 *   컴포넌트 → useProgressStore() → storage adapter → localStorage
 *                                                    ↕  (미래: API)
 *                                                    DB (PostgreSQL 등)
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */

import { create } from 'zustand'

// ────────────────────────────────────────────────────────────────
// 타입 정의 (DB 스키마와 1:1 대응 — 마이그레이션 용이)
// ────────────────────────────────────────────────────────────────

/** 학습 단계(Step) 진도 레코드 */
export interface StepProgress {
  stepId: string          // 'LS01' ~ 'LS12'
  status: 'locked' | 'in_progress' | 'completed' | 'mastered'
  completedAt: string | null   // ISO 8601
  practiceAnswered: boolean    // 연습문제 풀었는지
}

/** 문제풀이 탭 진도 */
export interface ProblemProgress {
  problemId: string       // 'P01' ~ 'P15'
  viewed: boolean
  solved: boolean
  solutionOpened: boolean
  hintOpened: boolean
}

/** 실전 TEST 결과 레코드 */
export interface TestRecord {
  id: string              // UUID 역할 (타임스탬프 기반)
  unitId: string          // 'F2-1'
  attemptNo: number       // 1회, 2회, ...
  score: number           // 0~100
  correctCount: number
  totalCount: number
  durationSec: number
  answers: (number | null)[]   // 문항별 선택지 인덱스 (null = 미응답)
  wrongConceptIds: string[]    // 틀린 문제의 concept 배열
  grade: string           // 'A+' | 'A' | 'B' | 'C' | 'D' | 'F'
  passed: boolean         // score >= PASS_SCORE
  takenAt: string         // ISO 8601
}

/** 오개념 누적 트래킹 */
export interface MisconceptionRecord {
  code: string            // 'M01' ~ 'M08'
  label: string
  hitCount: number        // 누적 틀린 횟수
  lastSeenAt: string | null
}

/** 학습 활동 피드 아이템 */
export interface ActivityItem {
  type: 'step_completed' | 'problem_solved' | 'test_taken' | 'practice_answered'
  unitId: string
  label: string           // 표시 텍스트 (e.g. "LS04 완료")
  score?: number
  at: string              // ISO 8601
}

/** 단원(Unit) 전체 진도 */
export interface UnitProgress {
  unitId: string
  // 개념학습
  learnSteps: Record<string, StepProgress>
  learnCompletedCount: number   // 완료된 step 수
  // 문제풀이
  problems: Record<string, ProblemProgress>
  // 실전 TEST
  testRecords: TestRecord[]
  bestScore: number
  passed: boolean               // bestScore >= PASS_SCORE
  // 오개념
  misconceptions: Record<string, MisconceptionRecord>
  // 메타
  startedAt: string | null
  completedAt: string | null
  lastAccessedAt: string
}

/** 연속 학습 스트릭 */
export interface StudyStreak {
  currentStreak: number       // 오늘 포함 연속 학습일 수
  longestStreak: number       // 역대 최장 스트릭
  lastStudyDate: string | null  // 'YYYY-MM-DD' 형식
  studyDates: string[]        // 학습한 날짜 배열 (최근 30일)
}

/** 전체 학생 진도 (localStorage 루트 키) */
export interface StudentProgress {
  studentId: string
  version: number         // 스키마 버전 — 마이그레이션 시 사용
  units: Record<string, UnitProgress>
  streak: StudyStreak
  recentActivity: ActivityItem[]   // 최근 20개 활동
  totalXp: number         // 누적 XP (RPG 시스템)
  updatedAt: string
}

// ────────────────────────────────────────────────────────────────
// 상수
// ────────────────────────────────────────────────────────────────
export const PASS_SCORE = 70       // TEST 합격 기준 점수
export const TOTAL_LEARN_STEPS = 12
const STORAGE_KEY = 'math_tutor_progress_v2'   // v2 — streak 필드 추가
const SCHEMA_VERSION = 2

// ── RPG 레벨/XP 시스템 ────────────────────────────────────────
export const XP_REWARDS = {
  STEP_COMPLETE:    50,   // 개념 학습 단계 완료
  PRACTICE_ANSWER:  20,   // 연습문제 풀기
  PROBLEM_SOLVE:    30,   // 문제풀이 완료
  TEST_PASS:       150,   // TEST 70점 이상 합격
  TEST_TAKE:        30,   // TEST 응시 (불합격도 지급)
  STREAK_BONUS:     10,   // 연속 학습일 보너스 (매일)
} as const

/** 레벨별 필요 총 XP — 레벨 N 달성에 필요한 누적 XP */
export const LEVEL_XP_TABLE: number[] = [
  0,    // Lv0  (초기값, 사용 안 함)
  0,    // Lv1  수학 입문자
  100,  // Lv2  개념 탐험가
  250,  // Lv3  수식 도전자
  500,  // Lv4  함수 견습생
  800,  // Lv5  기울기 탐험가
  1200, // Lv6  변화율 이해자
  1700, // Lv7  그래프 마스터
  2300, // Lv8  방정식 해결사
  3000, // Lv9  수포자 탈출 직전
  4000, // Lv10 수포자 탈출 ★
  5500, // Lv11 수학 실력자
  7500, // Lv12 수학 고수
  10000,// Lv13 함수 마스터
  13000,// Lv14 수학 천재
  17000,// Lv15 수학 영웅
]

export const LEVEL_TITLES: Record<number, string> = {
  1:  '수학 입문자',
  2:  '개념 탐험가',
  3:  '수식 도전자',
  4:  '함수 견습생',
  5:  '기울기 탐험가',
  6:  '변화율 이해자',
  7:  '그래프 마스터',
  8:  '방정식 해결사',
  9:  '수포자 탈출 직전',
  10: '수포자 탈출 ★',
  11: '수학 실력자',
  12: '수학 고수',
  13: '함수 마스터',
  14: '수학 천재',
  15: '수학 영웅',
}

export const LEVEL_EMOJIS: Record<number, string> = {
  1: '🌱', 2: '🌿', 3: '📖', 4: '⚡', 5: '🔍',
  6: '📈', 7: '📊', 8: '🔧', 9: '🔥', 10: '🏆',
  11: '⭐', 12: '💫', 13: '🎯', 14: '🧠', 15: '👑',
}

/** 총 XP → 레벨 계산 */
export function calcLevel(totalXp: number): number {
  let lv = 1
  for (let i = LEVEL_XP_TABLE.length - 1; i >= 1; i--) {
    if (totalXp >= LEVEL_XP_TABLE[i]) { lv = i; break }
  }
  return lv
}

/** 현재 레벨에서 다음 레벨까지 진척도 (0~1) */
export function calcLevelProgress(totalXp: number): { level: number; pct: number; currentXp: number; nextXp: number } {
  const level = calcLevel(totalXp)
  const currentFloor = LEVEL_XP_TABLE[level] ?? 0
  const nextCeil = LEVEL_XP_TABLE[level + 1] ?? LEVEL_XP_TABLE[LEVEL_XP_TABLE.length - 1]
  const range = nextCeil - currentFloor
  const pct = range > 0 ? Math.min(1, (totalXp - currentFloor) / range) : 1
  return { level, pct, currentXp: totalXp - currentFloor, nextXp: range }
}

const MISCONCEPTION_LABELS: Record<string, string> = {
  M01: '기울기를 단순 수로 오해',
  M02: 'x/y 역할 혼동',
  M03: 'y절편과 기울기 혼동',
  M04: '정비례 = 1차 함수 혼동',
  M05: '음수 기울기 = 함수 아님',
  M06: '표에서 변화율 오류',
  M07: '그래프↔식 변환 오류',
  M08: '문장제 방향성 오류',
}

// ────────────────────────────────────────────────────────────────
// 스트릭 헬퍼
// ────────────────────────────────────────────────────────────────
function todayStr(): string {
  return new Date().toISOString().slice(0, 10)  // 'YYYY-MM-DD'
}

function updateStreak(prev: StudyStreak): StudyStreak {
  const today = todayStr()
  if (prev.lastStudyDate === today) return prev  // 오늘 이미 기록됨

  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  const yesterdayStr = yesterday.toISOString().slice(0, 10)

  const newCurrent =
    prev.lastStudyDate === yesterdayStr
      ? prev.currentStreak + 1   // 연속
      : 1                         // 리셋

  // 최근 30일 날짜 목록 유지
  const newDates = Array.from(new Set([today, ...prev.studyDates])).slice(0, 30)

  return {
    currentStreak: newCurrent,
    longestStreak: Math.max(prev.longestStreak, newCurrent),
    lastStudyDate: today,
    studyDates: newDates,
  }
}

// ────────────────────────────────────────────────────────────────
// 스토리지 어댑터 (여기만 수정하면 DB 전환 완료)
// ────────────────────────────────────────────────────────────────
const storage = {
  /**
   * 진도 불러오기
   * [DB 전환 시] return await fetch('/api/progress').then(r=>r.json())
   */
  load(): StudentProgress | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) {
        // v1 데이터 마이그레이션 시도
        const v1raw = localStorage.getItem('math_tutor_progress_v1')
        if (v1raw) {
          const v1 = JSON.parse(v1raw) as Omit<StudentProgress, 'streak' | 'recentActivity' | 'version'>
          const migrated: StudentProgress = {
            ...v1,
            version: SCHEMA_VERSION,
            streak: makeDefaultStreak(),
            recentActivity: [],
          }
          storage.save(migrated)
          return migrated
        }
        return null
      }
      const parsed = JSON.parse(raw) as StudentProgress
      if (parsed.version !== SCHEMA_VERSION) return null
      return parsed
    } catch {
      return null
    }
  },

  /**
   * 진도 저장
   * [DB 전환 시] await fetch('/api/progress', { method:'PUT', body:JSON.stringify(data) })
   */
  save(data: StudentProgress): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...data, updatedAt: new Date().toISOString() }))
    } catch {
      console.warn('[progressStore] 저장 실패 — localStorage 용량 초과?')
    }
  },

  clear(): void {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem('math_tutor_progress_v1')
  },
}

// ────────────────────────────────────────────────────────────────
// 초기값 생성 헬퍼
// ────────────────────────────────────────────────────────────────
function makeDefaultStreak(): StudyStreak {
  return { currentStreak: 0, longestStreak: 0, lastStudyDate: null, studyDates: [] }
}

function makeDefaultUnitProgress(unitId: string): UnitProgress {
  const now = new Date().toISOString()
  return {
    unitId,
    learnSteps: {},
    learnCompletedCount: 0,
    problems: {},
    testRecords: [],
    bestScore: 0,
    passed: false,
    misconceptions: {},
    startedAt: null,
    completedAt: null,
    lastAccessedAt: now,
  }
}

function makeDefaultStudentProgress(studentId: string): StudentProgress {
  return {
    studentId,
    version: SCHEMA_VERSION,
    units: {},
    streak: makeDefaultStreak(),
    recentActivity: [],
    totalXp: 0,
    updatedAt: new Date().toISOString(),
  }
}

// ────────────────────────────────────────────────────────────────
// 활동 피드 헬퍼
// ────────────────────────────────────────────────────────────────
function addActivity(
  prev: ActivityItem[],
  item: Omit<ActivityItem, 'at'>
): ActivityItem[] {
  const newItem: ActivityItem = { ...item, at: new Date().toISOString() }
  return [newItem, ...prev].slice(0, 20)  // 최근 20개 유지
}

// ────────────────────────────────────────────────────────────────
// Zustand 스토어
// ────────────────────────────────────────────────────────────────
interface ProgressState {
  data: StudentProgress
  // ── 수포자 모드 ────────────────────────────────────────────
  supojaMode: boolean
  supojaOnboardingDone: boolean
  setSupojaMode: (on: boolean) => void
  setSupojaOnboardingDone: () => void
  // ── RPG 레벨업 알림 ────────────────────────────────────────
  levelUpEvent: { newLevel: number; title: string; emoji: string } | null
  clearLevelUpEvent: () => void
  // 유틸
  getUnit: (unitId: string) => UnitProgress
  // 개념학습
  completeStep: (unitId: string, stepId: string, stepTitle?: string) => void
  markPracticeAnswered: (unitId: string, stepId: string) => void
  // 문제풀이
  markProblemSolved: (unitId: string, problemId: string, opts?: { hintOpened?: boolean; solutionOpened?: boolean; label?: string }) => void
  // 실전 TEST
  recordTestResult: (unitId: string, result: Omit<TestRecord, 'id' | 'attemptNo' | 'takenAt'>) => TestRecord
  // 오개념
  recordMisconceptions: (unitId: string, codes: string[]) => void
  // 리셋 (개발/테스트용)
  resetUnit: (unitId: string) => void
  resetAll: () => void
  // 초기화 (앱 시작 시)
  init: (studentId: string) => void
}

// 수포자 모드 별도 localStorage 키
const SUPOJA_KEY = 'math_supoja_mode_v1'
function loadSupojaState(): { mode: boolean; onboardingDone: boolean } {
  try { return JSON.parse(localStorage.getItem(SUPOJA_KEY) ?? 'null') ?? { mode: false, onboardingDone: false } }
  catch { return { mode: false, onboardingDone: false } }
}
function saveSupojaState(mode: boolean, onboardingDone: boolean) {
  try { localStorage.setItem(SUPOJA_KEY, JSON.stringify({ mode, onboardingDone })) } catch { /* noop */ }
}

export const useProgressStore = create<ProgressState>((set, get) => ({
  data: makeDefaultStudentProgress('student_001'),

  // ── 수포자 모드 ────────────────────────────────────────────────
  supojaMode: loadSupojaState().mode,
  supojaOnboardingDone: loadSupojaState().onboardingDone,
  setSupojaMode(on: boolean) {
    const { supojaOnboardingDone } = get()
    saveSupojaState(on, supojaOnboardingDone)
    set({ supojaMode: on })
  },
  setSupojaOnboardingDone() {
    const { supojaMode } = get()
    saveSupojaState(supojaMode, true)
    set({ supojaOnboardingDone: true })
  },

  // ── RPG 레벨업 이벤트 ──────────────────────────────────────────
  levelUpEvent: null,
  clearLevelUpEvent() { set({ levelUpEvent: null }) },

  // ── 초기화 ───────────────────────────────────────────────────
  init(studentId: string) {
    const loaded = storage.load()
    if (loaded && loaded.studentId === studentId) {
      set({ data: loaded })
    } else {
      const fresh = makeDefaultStudentProgress(studentId)
      set({ data: fresh })
      storage.save(fresh)
    }
  },

  // ── 유닛 진도 가져오기 (없으면 생성) ─────────────────────────
  getUnit(unitId: string): UnitProgress {
    return get().data.units[unitId] ?? makeDefaultUnitProgress(unitId)
  },

  // ── 개념학습 단계 완료 ────────────────────────────────────────
  completeStep(unitId: string, stepId: string, stepTitle?: string) {
    set(state => {
      const unit = state.data.units[unitId] ?? makeDefaultUnitProgress(unitId)
      const now = new Date().toISOString()

      const prevStep = unit.learnSteps[stepId]
      if (prevStep?.status === 'completed' || prevStep?.status === 'mastered') {
        const updated = { ...unit, lastAccessedAt: now }
        return _persist(state, unitId, updated)
      }

      const newStep: StepProgress = {
        stepId, status: 'completed', completedAt: now,
        practiceAnswered: prevStep?.practiceAnswered ?? false,
      }
      const newSteps = { ...unit.learnSteps, [stepId]: newStep }
      const completedCount = Object.values(newSteps).filter(
        s => s.status === 'completed' || s.status === 'mastered'
      ).length

      const updated: UnitProgress = {
        ...unit, learnSteps: newSteps, learnCompletedCount: completedCount,
        startedAt: unit.startedAt ?? now, lastAccessedAt: now,
      }

      const newStreak = updateStreak(state.data.streak)
      const newActivity = addActivity(state.data.recentActivity, {
        type: 'step_completed', unitId,
        label: stepTitle ? `개념 완료: ${stepTitle}` : `개념 완료: ${stepId}`,
      })

      // XP 지급 + 레벨업 감지
      const xpGain = XP_REWARDS.STEP_COMPLETE
      const prevXp = state.data.totalXp ?? 0
      const newXp = prevXp + xpGain
      const prevLevel = calcLevel(prevXp)
      const newLevel = calcLevel(newXp)
      const levelUpEvent = newLevel > prevLevel
        ? { newLevel, title: LEVEL_TITLES[newLevel] ?? `레벨 ${newLevel}`, emoji: LEVEL_EMOJIS[newLevel] ?? '🎉' }
        : null

      const result = _persistFullXp(state, unitId, updated, newStreak, newActivity, newXp)
      return levelUpEvent ? { ...result, levelUpEvent } : result
    })
  },

  // ── 연습문제 풀이 기록 ────────────────────────────────────────
  markPracticeAnswered(unitId: string, stepId: string) {
    set(state => {
      const unit = state.data.units[unitId] ?? makeDefaultUnitProgress(unitId)
      const prev = unit.learnSteps[stepId] ?? {
        stepId, status: 'in_progress' as const, completedAt: null, practiceAnswered: false
      }
      if (prev.practiceAnswered) return _persist(state, unitId, unit) // 중복 방지

      const newSteps = { ...unit.learnSteps, [stepId]: { ...prev, practiceAnswered: true } }
      const newStreak = updateStreak(state.data.streak)
      const newActivity = addActivity(state.data.recentActivity, {
        type: 'practice_answered', unitId, label: `연습문제 완료: ${stepId}`,
      })

      const xpGain = XP_REWARDS.PRACTICE_ANSWER
      const prevXp = state.data.totalXp ?? 0
      const newXp = prevXp + xpGain
      const prevLevel = calcLevel(prevXp)
      const newLevel = calcLevel(newXp)
      const levelUpEvent = newLevel > prevLevel
        ? { newLevel, title: LEVEL_TITLES[newLevel] ?? `레벨 ${newLevel}`, emoji: LEVEL_EMOJIS[newLevel] ?? '🎉' }
        : null

      const result = _persistFullXp(
        state, unitId,
        { ...unit, learnSteps: newSteps, lastAccessedAt: new Date().toISOString() },
        newStreak, newActivity, newXp
      )
      return levelUpEvent ? { ...result, levelUpEvent } : result
    })
  },

  // ── 문제풀이 기록 ─────────────────────────────────────────────
  markProblemSolved(unitId, problemId, opts = {}) {
    set(state => {
      const unit = state.data.units[unitId] ?? makeDefaultUnitProgress(unitId)
      const prev = unit.problems[problemId] ?? { problemId, viewed: false, solved: false, solutionOpened: false, hintOpened: false }
      if (prev.solved) {
        // 이미 해결된 문제 — XP 중복 지급 없음
        const updated = { ...unit, problems: { ...unit.problems, [problemId]: { ...prev, viewed: true, hintOpened: opts.hintOpened ?? prev.hintOpened, solutionOpened: opts.solutionOpened ?? prev.solutionOpened } }, lastAccessedAt: new Date().toISOString() }
        return _persist(state, unitId, updated)
      }
      const updated = {
        ...unit,
        problems: { ...unit.problems, [problemId]: { ...prev, viewed: true, solved: true, hintOpened: opts.hintOpened ?? prev.hintOpened, solutionOpened: opts.solutionOpened ?? prev.solutionOpened } },
        lastAccessedAt: new Date().toISOString(),
      }
      const newStreak = updateStreak(state.data.streak)
      const newActivity = addActivity(state.data.recentActivity, {
        type: 'problem_solved', unitId, label: opts.label ?? `문제 해결: ${problemId}`,
      })

      const xpGain = XP_REWARDS.PROBLEM_SOLVE
      const prevXp = state.data.totalXp ?? 0
      const newXp = prevXp + xpGain
      const prevLevel = calcLevel(prevXp)
      const newLevel = calcLevel(newXp)
      const levelUpEvent = newLevel > prevLevel
        ? { newLevel, title: LEVEL_TITLES[newLevel] ?? `레벨 ${newLevel}`, emoji: LEVEL_EMOJIS[newLevel] ?? '🎉' }
        : null

      const result = _persistFullXp(state, unitId, updated, newStreak, newActivity, newXp)
      return levelUpEvent ? { ...result, levelUpEvent } : result
    })
  },

  // ── TEST 결과 저장 ────────────────────────────────────────────
  recordTestResult(unitId, result) {
    const now = new Date().toISOString()
    const unit = get().data.units[unitId] ?? makeDefaultUnitProgress(unitId)
    const attemptNo = unit.testRecords.length + 1
    const record: TestRecord = {
      ...result, id: `${unitId}_test_${Date.now()}`, attemptNo, takenAt: now,
    }
    const newBest = Math.max(unit.bestScore, result.score)
    const passed = newBest >= PASS_SCORE

    set(state => {
      const u = state.data.units[unitId] ?? makeDefaultUnitProgress(unitId)
      const updated: UnitProgress = {
        ...u, testRecords: [...u.testRecords, record], bestScore: newBest, passed,
        completedAt: passed && !u.completedAt ? now : u.completedAt, lastAccessedAt: now,
      }
      const newStreak = updateStreak(state.data.streak)
      const newActivity = addActivity(state.data.recentActivity, {
        type: 'test_taken', unitId,
        label: `TEST ${attemptNo}회차 — ${result.score}점 (${result.grade})`,
        score: result.score,
      })

      // XP: 합격 시 150, 응시만 해도 30
      const xpGain = result.passed ? XP_REWARDS.TEST_PASS : XP_REWARDS.TEST_TAKE
      const prevXp = state.data.totalXp ?? 0
      const newXp = prevXp + xpGain
      const prevLevel = calcLevel(prevXp)
      const newLevel = calcLevel(newXp)
      const levelUpEvent = newLevel > prevLevel
        ? { newLevel, title: LEVEL_TITLES[newLevel] ?? `레벨 ${newLevel}`, emoji: LEVEL_EMOJIS[newLevel] ?? '🎉' }
        : null

      const res = _persistFullXp(state, unitId, updated, newStreak, newActivity, newXp)
      return levelUpEvent ? { ...res, levelUpEvent } : res
    })
    return record
  },

  // ── 오개념 기록 ───────────────────────────────────────────────
  recordMisconceptions(unitId, codes) {
    if (codes.length === 0) return
    set(state => {
      const unit = state.data.units[unitId] ?? makeDefaultUnitProgress(unitId)
      const now = new Date().toISOString()
      const newMisconceptions = { ...unit.misconceptions }
      codes.forEach(code => {
        const prev = newMisconceptions[code] ?? { code, label: MISCONCEPTION_LABELS[code] ?? code, hitCount: 0, lastSeenAt: null }
        newMisconceptions[code] = { ...prev, hitCount: prev.hitCount + 1, lastSeenAt: now }
      })
      return _persist(state, unitId, { ...unit, misconceptions: newMisconceptions, lastAccessedAt: now })
    })
  },

  // ── 리셋 ─────────────────────────────────────────────────────
  resetUnit(unitId) {
    set(state => _persist(state, unitId, makeDefaultUnitProgress(unitId)))
  },

  resetAll() {
    storage.clear()
    const fresh = makeDefaultStudentProgress(get().data.studentId)
    set({ data: fresh })
    storage.save(fresh)
  },
}))

// ────────────────────────────────────────────────────────────────
// 내부 헬퍼: 상태 업데이트 + 스토리지 저장
// ────────────────────────────────────────────────────────────────
function _persist(
  state: { data: StudentProgress },
  unitId: string,
  unitData: UnitProgress
): { data: StudentProgress } {
  const newData: StudentProgress = {
    ...state.data,
    units: { ...state.data.units, [unitId]: unitData },
    updatedAt: new Date().toISOString(),
  }
  storage.save(newData)
  return { data: newData }
}

function _persistFull(
  state: { data: StudentProgress },
  unitId: string,
  unitData: UnitProgress,
  streak: StudyStreak,
  recentActivity: ActivityItem[]
): { data: StudentProgress } {
  const newData: StudentProgress = {
    ...state.data,
    units: { ...state.data.units, [unitId]: unitData },
    streak,
    recentActivity,
    updatedAt: new Date().toISOString(),
  }
  storage.save(newData)
  return { data: newData }
}

function _persistFullXp(
  state: { data: StudentProgress },
  unitId: string,
  unitData: UnitProgress,
  streak: StudyStreak,
  recentActivity: ActivityItem[],
  totalXp: number
): { data: StudentProgress } {
  const newData: StudentProgress = {
    ...state.data,
    units: { ...state.data.units, [unitId]: unitData },
    streak,
    recentActivity,
    totalXp,
    updatedAt: new Date().toISOString(),
  }
  storage.save(newData)
  return { data: newData }
}

// ────────────────────────────────────────────────────────────────
// 셀렉터 훅 (컴포넌트에서 편리하게 사용)
// ────────────────────────────────────────────────────────────────
export function useUnitProgress(unitId: string): UnitProgress {
  return useProgressStore(s => s.data.units[unitId] ?? makeDefaultUnitProgress(unitId))
}

export function useTestRecords(unitId: string): TestRecord[] {
  return useProgressStore(s => s.data.units[unitId]?.testRecords ?? [])
}

export function useMisconceptions(unitId: string): MisconceptionRecord[] {
  return useProgressStore(s =>
    Object.values(s.data.units[unitId]?.misconceptions ?? {}).sort((a, b) => b.hitCount - a.hitCount)
  )
}

export function useLearnProgress(unitId: string) {
  return useProgressStore(s => {
    const unit = s.data.units[unitId]
    return {
      completedCount: unit?.learnCompletedCount ?? 0,
      steps: unit?.learnSteps ?? {},
      pct: Math.round(((unit?.learnCompletedCount ?? 0) / TOTAL_LEARN_STEPS) * 100),
    }
  })
}

export function useStudyStreak(): StudyStreak {
  return useProgressStore(s => s.data.streak)
}

export function useRecentActivity(): ActivityItem[] {
  return useProgressStore(s => s.data.recentActivity)
}

/** RPG 레벨/XP 셀렉터 */
export function usePlayerLevel() {
  return useProgressStore(s => {
    const xp = s.data.totalXp ?? 0
    const info = calcLevelProgress(xp)
    return {
      totalXp: xp,
      level: info.level,
      pct: info.pct,
      currentXp: info.currentXp,
      nextXp: info.nextXp,
      title: LEVEL_TITLES[info.level] ?? `레벨 ${info.level}`,
      emoji: LEVEL_EMOJIS[info.level] ?? '🌱',
    }
  })
}
